import {
    Accordion,
    AccordionHeader,
    AccordionItem,
    AccordionPanel,
    Button,
    makeStyles,
    mergeClasses,
    Text,
    tokens,
} from '@fluentui/react-components';
import { CheckmarkCircle24Regular, CopyRegular, DismissCircle24Regular, SubtractCircle24Regular } from '@fluentui/react-icons';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ModalV9 from '../../components/common/ModalV9';
import { useToastMessage } from '../../hooks/useToastMessage';
import type { Pillar2FilingOperationResult } from '../../model/pillar2FilingOperationResult';
import { Pillar2FilingOperationStep } from '../../model/pillar2FilingOperationStep';
import type { Pillar2FilingResponse } from '../../model/pillar2FilingResponse';
import { Pillar2OperationStatus } from '../../model/pillar2OperationStatus';
import {
    PILLAR2_FILING_STEP_ORDER,
    PILLAR2_RESPONSE_FILE_PREFIX,
    PILLAR2_SUBMISSION_FILE_PREFIX,
} from './utils/pillar2FilingResultConstants';
import { buildPillar2FilingDownloadFilename, downloadFilingContentFile, formatFilingContent } from './utils/pillar2FilingResultUtils';

const useStyles = makeStyles({
    cardStack: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        width: '100%',
        overflow: 'auto',
        maxHeight: '550px',
    },
    stepStack: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        width: '100%',
    },
    stepRow: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        backgroundColor: tokens.colorNeutralBackground1,
    },
    stepRowTitleSkipped: {
        fontStyle: 'italic',
        color: tokens.colorNeutralForeground4,
    },
    stepRowTitleSkippedStrike: {
        textDecoration: 'line-through',
    },
    stepRowHeader: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        minWidth: 0,
        padding: `${tokens.spacingVerticalXS} 0`,
    },
    stepAccordion: {
        width: '100%',
        '& .fui-AccordionHeader__button': {
            minHeight: '32px',
            padding: `${tokens.spacingVerticalXS} 0`,
            backgroundColor: tokens.colorNeutralBackground1,
        },
    },
    resultCard: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        padding: tokens.spacingVerticalXS,
        borderRadius: tokens.borderRadiusMedium,
        backgroundColor: tokens.colorNeutralBackground3,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
    resultCardAccordion: {
        width: '100%',
    },
    accordionHeaderRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: tokens.spacingHorizontalM,
    },
    accordionTitleRow: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        minWidth: 0,
    },
    accordionViewDetails: {
        flexShrink: 0,
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground2,
        fontWeight: tokens.fontWeightRegular,
    },
    stepIcon: {
        flexShrink: 0,
        fontSize: '20px',
    },
    codeBlock: {
        width: '100%',
        marginBottom: tokens.spacingVerticalS,
        ':last-child': {
            marginBottom: 0,
        },
    },
    payloadHeaderRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalXS,
        width: '100%',
    },
    codePre: {
        display: 'block',
        width: '100%',
        maxHeight: '240px',
        minHeight: 0,
        overflow: 'auto',
        resize: 'none',
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: tokens.fontSizeBase200,
        lineHeight: tokens.lineHeightBase200,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        margin: 0,
        padding: tokens.spacingVerticalM,
        borderRadius: tokens.borderRadiusSmall,
        backgroundColor: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        marginBottom: tokens.spacingVerticalM,
    },
    modalActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalS,
        justifyContent: 'flex-end',
    },
    iconSuccess: {
        color: tokens.colorPaletteGreenForeground1,
    },
    iconFailed: {
        color: tokens.colorPaletteRedForeground1,
    },
    iconSkipped: {
        color: tokens.colorNeutralForeground4,
    },
});

function resolveModalTitle(response: Pillar2FilingResponse, t: (key: string) => string): string {
    const submitOp = response.operations?.find((o) => o.step === Pillar2FilingOperationStep.Submit);
    if (submitOp?.status === Pillar2OperationStatus.Failed) {
        return t('pillar2FilingResultTitleSubmitFailed');
    }
    if (response.isSuccess) {
        return t('pillar2FilingResultTitleSuccess');
    }
    if (submitOp?.status === Pillar2OperationStatus.Success) {
        return t('pillar2FilingResultTitlePartialErrors');
    }
    return t('pillar2FilingResultTitleFailure');
}

function StepIcon({ status }: { status?: Pillar2OperationStatus }) {
    const styles = useStyles();
    if (status === Pillar2OperationStatus.Failed) {
        return <DismissCircle24Regular className={mergeClasses(styles.stepIcon, styles.iconFailed)} />;
    }
    if (status === Pillar2OperationStatus.Success) {
        return <CheckmarkCircle24Regular className={mergeClasses(styles.stepIcon, styles.iconSuccess)} />;
    }
    return <SubtractCircle24Regular className={mergeClasses(styles.stepIcon, styles.iconSkipped)} />;
}

interface CodeBlockContainerProps {
    text: string;
    onCopy: () => void;
}

const CodeBlockContainer: React.FC<CodeBlockContainerProps> = ({ text, onCopy }) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const copyLabel = t('pillar2ModalCopyButton').toString();
    return (
        <div className={styles.codeBlock}>
            <div className={styles.payloadHeaderRow}>
                <Button appearance='secondary' icon={<CopyRegular />} onClick={onCopy} aria-label={copyLabel}>
                    {copyLabel}
                </Button>
            </div>
            <pre className={styles.codePre}>{text}</pre>
        </div>
    );
};

interface OperationStepRowProps {
    itemValue: string;
    title: string;
    status?: Pillar2OperationStatus;
    message?: string | null;
    onCopyText: (text: string) => void;
}

const OperationStepRow: React.FC<OperationStepRowProps> = ({ itemValue, title, status, message, onCopyText }) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const viewDetailsLabel = t('pillar2FilingResultViewDetails').toString();
    const hasError = status === Pillar2OperationStatus.Failed;
    const isSkipped = status === Pillar2OperationStatus.Skipped;
    const statusSuffix = isSkipped ? t('pillar2FilingResultStepSkippedReason', { message }).toString() : '';
    const rowClassName = mergeClasses(styles.stepRow);
    const titleClassName = isSkipped ? styles.stepRowTitleSkipped : undefined;
    const stepTitle = (
        <Text weight='semibold' className={titleClassName}>
            {isSkipped ? <span className={styles.stepRowTitleSkippedStrike}>{title}</span> : title}
            {statusSuffix ? ` (${statusSuffix})` : ''}
        </Text>
    );

    if (!hasError) {
        return (
            <div className={rowClassName}>
                <div className={styles.stepRowHeader}>
                    {status ? <StepIcon status={status} /> : null}
                    {stepTitle}
                </div>
            </div>
        );
    }

    return (
        <div className={rowClassName}>
            <Accordion collapsible className={styles.stepAccordion} defaultOpenItems={[itemValue]}>
                <AccordionItem value={itemValue}>
                    <AccordionHeader expandIconPosition='end'>
                        <div className={styles.accordionHeaderRow}>
                            <div className={styles.accordionTitleRow}>
                                <StepIcon status={status} />
                                {stepTitle}
                            </div>
                            <span className={styles.accordionViewDetails}>{viewDetailsLabel}</span>
                        </div>
                    </AccordionHeader>
                    <AccordionPanel>
                        {message ? <CodeBlockContainer text={message} onCopy={() => onCopyText(message)} /> : null}
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </div>
    );
};

interface ResultCardSectionProps {
    itemValue: string;
    title: string;
    status?: Pillar2OperationStatus;
    codeText?: string;
    defaultExpanded?: boolean;
    onCopyText: (text: string) => void;
}

const ResultCardSection: React.FC<ResultCardSectionProps> = ({
    itemValue,
    title,
    status,
    codeText,
    defaultExpanded = false,
    onCopyText,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const viewDetailsLabel = t('pillar2FilingResultViewDetails').toString();
    return (
        <div className={styles.resultCard}>
            <Accordion collapsible className={styles.resultCardAccordion} defaultOpenItems={defaultExpanded ? [itemValue] : []}>
                <AccordionItem value={itemValue}>
                    <AccordionHeader expandIconPosition='end'>
                        <div className={styles.accordionHeaderRow}>
                            <div className={styles.accordionTitleRow}>
                                {status ? <StepIcon status={status} /> : null}
                                <Text weight='semibold'>{title}</Text>
                            </div>
                            <span className={styles.accordionViewDetails}>{viewDetailsLabel}</span>
                        </div>
                    </AccordionHeader>
                    <AccordionPanel>
                        {codeText ? <CodeBlockContainer text={codeText} onCopy={() => onCopyText(codeText)} /> : null}
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </div>
    );
};

export interface Pillar2FilingResultModalProps {
    isOpen: boolean;
    response: Pillar2FilingResponse | null;
    returnName?: string | null;
    onClose: () => void;
}

export const Pillar2FilingResultModal: React.FC<Pillar2FilingResultModalProps> = ({ isOpen, response, returnName, onClose }) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const { showToast } = useToastMessage();

    const stepLabel = useCallback(
        (step?: Pillar2FilingOperationStep, status?: Pillar2OperationStatus) => {
            switch (step) {
                case Pillar2FilingOperationStep.Submit:
                    if (status === Pillar2OperationStatus.Success) {
                        return t('pillar2FilingResultStepSubmitSuccess').toString();
                    }
                    if (status === Pillar2OperationStatus.Failed) {
                        return t('pillar2FilingResultStepSubmitFailed').toString();
                    }
                    return t('pillar2FilingResultStepSubmitSuccess').toString();
                case Pillar2FilingOperationStep.CloudStorage:
                    if (status === Pillar2OperationStatus.Success) {
                        return t('pillar2FilingResultStepCloudStorageSuccess').toString();
                    }
                    if (status === Pillar2OperationStatus.Failed) {
                        return t('pillar2FilingResultStepCloudStorageFailed').toString();
                    }
                    if (status === Pillar2OperationStatus.Skipped) {
                        return t('pillar2FilingResultStepCloudStorage').toString();
                    }
                    return t('pillar2FilingResultStepCloudStorageSuccess').toString();
                default:
                    return step ?? '';
            }
        },
        [t]
    );

    const orderedOperations = useMemo(() => {
        const ops = response?.operations ?? [];
        return PILLAR2_FILING_STEP_ORDER.map((step) => ops.find((o) => o.step === step)).filter(
            (o): o is Pillar2FilingOperationResult => o != null
        );
    }, [response?.operations]);

    const title = response ? resolveModalTitle(response, (key) => t(key).toString()) : '';

    const submissionPayloadText = response ? formatFilingContent(response.submissionPayload) : '';
    const submissionResponseText = response ? formatFilingContent(response.submissionResponse) : '';

    const hasPayloads = submissionPayloadText.length > 0 || submissionResponseText.length > 0;

    const copyToClipboard = useCallback(
        async (text: string) => {
            try {
                await navigator.clipboard.writeText(text);
                showToast(
                    t('pillar2ErrorModalCopySuccessTitle').toString(),
                    t('pillar2ErrorModalCopySuccessDescription').toString(),
                    'success'
                );
            } catch {
                showToast(t('pillar2ErrorModalCopyErrorTitle').toString(), t('pillar2ErrorModalCopyErrorDescription').toString(), 'error');
            }
        },
        [showToast, t]
    );

    const handleDownloadFiles = useCallback(() => {
        if (submissionPayloadText) {
            downloadFilingContentFile(
                buildPillar2FilingDownloadFilename(PILLAR2_SUBMISSION_FILE_PREFIX, returnName),
                submissionPayloadText
            );
        }
        if (submissionResponseText) {
            downloadFilingContentFile(buildPillar2FilingDownloadFilename(PILLAR2_RESPONSE_FILE_PREFIX, returnName), submissionResponseText);
        }
    }, [submissionPayloadText, submissionResponseText, returnName]);

    const bodyContent = response ? (
        <div className={styles.cardStack}>
            <div className={styles.stepStack}>
                {orderedOperations.map((op) => {
                    return (
                        <OperationStepRow
                            key={op.step}
                            itemValue={`step-${op.step}`}
                            title={stepLabel(op.step, op.status)}
                            status={op.status}
                            message={op.message}
                            onCopyText={(text) => void copyToClipboard(text)}
                        />
                    );
                })}
            </div>
            {submissionPayloadText ? (
                <ResultCardSection
                    itemValue='submission'
                    title={t('pillar2FilingResultSubmissionPayload').toString()}
                    codeText={submissionPayloadText}
                    onCopyText={(text) => void copyToClipboard(text)}
                />
            ) : null}
            {submissionResponseText ? (
                <ResultCardSection
                    itemValue='response'
                    title={t('pillar2FilingResultResponsePayload').toString()}
                    codeText={submissionResponseText}
                    defaultExpanded
                    onCopyText={(text) => void copyToClipboard(text)}
                />
            ) : null}
        </div>
    ) : null;

    return (
        <ModalV9
            isOpen={isOpen}
            onClose={onClose}
            isBlocking
            showModalCloseButton={true}
            modalMaxWidthInPixels={760}
            modalMaxHeightInPixels={680}
            zIndex={1000002}
            headerContent={title}
            bodyContent={bodyContent}
            buttons={
                <div className={styles.modalActions}>
                    {hasPayloads ? (
                        <Button
                            appearance='secondary'
                            onClick={handleDownloadFiles}
                            aria-label={t('pillar2FilingResultDownloadFiles').toString()}
                        >
                            {t('pillar2FilingResultDownloadFiles').toString()}
                        </Button>
                    ) : null}
                    <Button appearance='primary' onClick={onClose} aria-label={t('ok').toString()}>
                        {t('ok').toString()}
                    </Button>
                </div>
            }
        />
    );
};

export default Pillar2FilingResultModal;
